import React from "react";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, ExternalLink } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Home", href: "/#home" },
    { name: "Services", href: "/#services" },
    { name: "Team", href: "/#team" },
    { name: "Blog", href: "/#blog" },
    { name: "Testimonials", href: "/#testimonials" },
    { name: "Contact", href: "/#contact" },
  ];

  const services = [
    { name: "Physiotherapy", href: "#services" },
    { name: "Massage Therapy", href: "#services" },
    { name: "Kinesiology", href: "#services" },
    { name: "Acupuncture", href: "#services" },
    { name: "HeadWorks TMD", href: "#headworks" },
  ];

  return (
    <footer className="bg-[#0F4C3A] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <div className="flex flex-col gap-3" data-testid="footer-logo">
              <img
                src="/assets/bc-ice-logo-dark-bg.png"
                alt="BC Injury Care & Exercise"
                className="h-11 w-auto"
                loading="lazy"
                decoding="async"
              />
              <div className="flex flex-col leading-tight">
                <span
                  className="font-extrabold tracking-tight text-white text-lg"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  BC Injury Care <span className="text-[#F97316]">&amp;</span> Exercise
                </span>
                <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-white/60 mt-0.5">
                  BCICE · Burnaby, BC
                </span>
              </div>
            </div>
            <p className="text-white/80 leading-relaxed">
              Providing expert physiotherapy, massage therapy, kinesiology, 
              acupuncture, and specialized TMD treatment in Burnaby, BC.
            </p>
            <a
              href="https://bc-ice.janeapp.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C2410C] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#9A3412] transition-colors"
              data-testid="footer-book-btn"
            >
              Book Appointment
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-lg mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <a
                    href={service.href}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C2410C] mt-0.5 flex-shrink-0" />
                <a 
                  href="https://maps.google.com/?q=7885+6th+St+%23201,+Burnaby,+BC+V3N+3N4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  7885 6th St #201<br />Burnaby, BC V3N 3N4
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#C2410C] flex-shrink-0" />
                <a href="tel:+16045534000" className="text-white/80 hover:text-white transition-colors">
                  (604) 553-4000
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#C2410C] flex-shrink-0" />
                <a href="mailto:appointments@bc-ice.ca" className="text-white/80 hover:text-white transition-colors">
                  appointments@bc-ice.ca
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#C2410C] mt-0.5 flex-shrink-0" />
                <div className="text-white/80">
                  <p>Mon-Fri: 9AM - 6PM</p>
                  <p>Sat: By appointment</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/60 text-sm">
            © {currentYear} BC-ICE. All rights reserved. | ICBC & WorkSafe BC Direct Billing Available
          </p>
          <div className="flex items-center gap-4">
            <a
              href="http://www.headworksphysio.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white text-sm"
            >
              HeadWorks Physio
            </a>
            <span className="text-white/30">|</span>
            <a
              href="https://bc-ice.janeapp.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white text-sm flex items-center gap-1"
            >
              Powered by Jane App
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
